import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface FeedPost {
  id: string;
  user_id: string;
  outfit_data: {
    top: any;
    bottom: any;
    shoes: any;
    description: string;
    score: number;
    styleTip: string;
  };
  caption: string | null;
  likes_count: number;
  created_at: string;
  profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
  user_liked?: boolean;
}

export function useFeed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    const { data: postsData, error: postsError } = await supabase
      .from('feed_posts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (postsError) {
      console.error('Error fetching posts:', postsError);
      setLoading(false);
      return;
    }

    // Fetch profiles for all posts using public_profiles view
    const userIds = [...new Set(postsData.map(p => p.user_id))];
    const { data: profilesData } = await supabase
      .from('public_profiles' as any)
      .select('id, display_name, avatar_url')
      .in('id', userIds) as { data: Array<{ id: string; display_name: string | null; avatar_url: string | null }> | null };

    const profilesMap = new Map((profilesData || []).map(p => [p.id, p]));

    // If logged in, fetch user's likes
    let userLikes = new Set<string>();
    if (user) {
      const { data: likesData } = await supabase
        .from('feed_likes')
        .select('post_id')
        .eq('user_id', user.id);
      
      userLikes = new Set(likesData?.map(l => l.post_id) || []);
    }

    const enrichedPosts: FeedPost[] = postsData.map(post => ({
      ...post,
      outfit_data: post.outfit_data as FeedPost['outfit_data'],
      profile: profilesMap.get(post.user_id),
      user_liked: userLikes.has(post.id),
    }));

    setPosts(enrichedPosts);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const createPost = async (outfitData: FeedPost['outfit_data'], caption?: string) => {
    if (!user) {
      toast.error('Please sign in to share');
      return null;
    }

    const { data, error } = await supabase
      .from('feed_posts')
      .insert({
        user_id: user.id,
        outfit_data: outfitData,
        caption,
      })
      .select()
      .single();

    if (error) {
      toast.error('Failed to share outfit');
      return null;
    }

    toast.success('Shared with community!');
    fetchPosts();
    return data;
  };

  const awardPointsToPostOwner = async (postOwnerId: string) => {
    // Award 5 Aura points to the post owner for receiving a like
    try {
      const { data: ownerProfile } = await supabase
        .from('profiles')
        .select('aura_points')
        .eq('id', postOwnerId)
        .single();

      if (ownerProfile) {
        const newPoints = (ownerProfile.aura_points || 0) + 5;
        await supabase
          .from('profiles')
          .update({ aura_points: newPoints })
          .eq('id', postOwnerId);
      }
    } catch (error) {
      console.error('Error awarding points to post owner:', error);
    }
  };

  const likePost = async (postId: string) => {
    if (!user) {
      toast.error('Please sign in to like');
      return;
    }

    const post = posts.find(p => p.id === postId);
    if (!post) return;

    if (post.user_liked) {
      // Unlike
      await supabase
        .from('feed_likes')
        .delete()
        .eq('user_id', user.id)
        .eq('post_id', postId);

      setPosts(prev => prev.map(p => 
        p.id === postId 
          ? { ...p, likes_count: p.likes_count - 1, user_liked: false }
          : p
      ));
    } else {
      // Like
      await supabase
        .from('feed_likes')
        .insert({ user_id: user.id, post_id: postId });

      setPosts(prev => prev.map(p => 
        p.id === postId 
          ? { ...p, likes_count: p.likes_count + 1, user_liked: true }
          : p
      ));

      // Award points to post owner (only when liking, not unliking)
      if (post.user_id !== user.id) {
        await awardPointsToPostOwner(post.user_id);
      }
    }
  };

  const deletePost = async (postId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('feed_posts')
      .delete()
      .eq('id', postId)
      .eq('user_id', user.id);

    if (error) {
      toast.error('Failed to delete post');
      return;
    }

    setPosts(prev => prev.filter(p => p.id !== postId));
    toast.success('Post deleted');
  };

  return {
    posts,
    loading,
    createPost,
    likePost,
    deletePost,
    refetch: fetchPosts,
  };
}
